'use client';

import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { websiteEnv } from '@/constants/website-env';
import { useLoadingRouter } from '@/hooks/use-loading-router';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import HoverPrefetchLink from '../hover-prefetch-link';
import CategoryFilterItem from './category-filter-item';
import ColorFilterItem from './color-filter-item';
import CurrentFilterItems from './current-filter-items';
import DefaultFilterItem from './default-filter-item';
import DressStyleFilterItem from './dress-style-filter-item';
import { generateLinkHref } from './generate-link-href';
import { generateNextState } from './generate-next-filter-state';
import PriceSelect from './price-filter-item';
import SizeFilterItem from './size-filter-item';
import StockFilterItem from './stock-filter-item';

interface Props {
  filterOptions: {
    attribute_code: string;
    count: number;
    label: string;
    position: number;
    options: {
      count: number;
      label: string;
      swatch_data: { thumbnail: string | null; value: string | null } | null;
      value: string;
      parent_id: number;
      url_key: string;
    }[];
  }[];
  filterState: FilterState;
  curCategoryUrlKey: string;
  isSearchPage: boolean;
  hideColorsFamily: boolean;
  optionIdMapToRewrite: Record<string, Record<string, string>>;
  onSetFilterState: (nextFilterState: FilterState) => void;
  searchParams?: URLSearchParams;
  headerVisibleHeight: number;
}

const FilterAside = (props: Props) => {
  const {
    filterOptions,
    filterState,
    curCategoryUrlKey,
    hideColorsFamily,
    isSearchPage,
    optionIdMapToRewrite,
    onSetFilterState,
    searchParams,
    headerVisibleHeight,
  } = props;

  const inStockFilter = filterOptions.find(x => x.attribute_code === 'aw_product_stock_condition')?.options?.[0];
  const inStockFilterValue = inStockFilter?.value;
  const categoryOption = filterOptions.find(x => x.attribute_code === 'category_id');
  const categoryItemOptions = isSearchPage
    ? categoryOption?.options || []
    : (categoryOption?.options || []).filter((x, i, arr) => {
        return arr.some(item => String(item.value) === String(x.parent_id));
      });
  const colorsFamilyOptions = filterOptions.find(x => x.attribute_code === COLORS_FAMILY_KEY)?.options || [];
  const inStockStateIndex = filterState.findIndex(x => x.attributeCode === 'aw_product_stock_condition');

  const inStockFilterNextState = generateNextState({
    attributeCode: 'aw_product_stock_condition',
    value: inStockFilterValue || '',
    label: inStockFilter?.label || '',
    prevStateIndex: inStockStateIndex,
    prevActive: inStockStateIndex !== -1,
    filterState,
    optionIdMapToRewrite,
    isSearchPage,
    hideColorsFamily,
  });

  const clearAllLink = isSearchPage
    ? `/search.html` + (searchParams?.get('query') ? `?query=${searchParams?.get('query') || ''}` : '')
    : `/${curCategoryUrlKey}`;

  return (
    <div
      className=' sticky w-[269px] flex-none overflow-y-auto'
      id='category-filter-sidebar'
      style={{
        top: headerVisibleHeight,
        maxHeight: `calc(100vh - ${headerVisibleHeight}px)`,
      }}
    >
      <div className='flex flex-col pr-2'>
        <div className='[&_+_div]_border-t-[0]'>
          <div className=' flex items-center justify-between border-b-1 border-solid border-E7DED5 py-2.5 '>
            <span className='font-Shango text-[25px] leading-[30px]'>FILTERS</span>
            {filterState.length !== 0 ? (
              <HoverPrefetchLink
                className='text-xs leading-[18px] text-aba6a0'
                href={clearAllLink}
              >
                Clear All
              </HoverPrefetchLink>
            ) : null}
          </div>

          <CurrentFilterItems
            filterState={filterState}
            curCategoryUrlKey={curCategoryUrlKey}
            isSearchPage={isSearchPage}
            hideColorsFamily={hideColorsFamily}
            optionIdMapToRewrite={optionIdMapToRewrite}
            colorsFamilyOptions={colorsFamilyOptions}
            searchParams={searchParams}
          />
        </div>
        {inStockFilterValue && websiteEnv.isUnitedStates ? (
          <StockFilterItem
            mode='link'
            href={generateLinkHref({
              categoryUrlKey: curCategoryUrlKey,
              filterState: inStockFilterNextState,
              isSearchPage,
              hideColorsFamily,
              colorsFamilyOptions,
              optionIdMapToRewrite,
              searchParams,
            })}
            isActive={inStockStateIndex !== -1}
            onClick={() => {
              onSetFilterState(inStockFilterNextState);
            }}
          />
        ) : null}

        {categoryOption && categoryItemOptions && categoryItemOptions.length > 0 ? (
          <FilterAsideItem
            isSearchPage={isSearchPage}
            defaultExpanded={
              filterState.some(x => x.attributeCode === 'category_id') ||
              categoryItemOptions.some(x => x.url_key === curCategoryUrlKey)
            }
            label={categoryOption.label}
            options={categoryItemOptions}
            attributeCode='category_id'
            curCategoryUrlKey={curCategoryUrlKey}
            filterState={filterState}
            itemCountToShow={isSearchPage ? 6 : Infinity}
            generateNextState={param =>
              generateNextState({
                ...param,
                filterState,
                optionIdMapToRewrite,
                isSearchPage,
                hideColorsFamily,
              })
            }
            onChangeFilter={nextFilterState => {
              onSetFilterState(nextFilterState);
            }}
            onGenerateLinkHref={nextState => {
              return generateLinkHref({
                categoryUrlKey: curCategoryUrlKey,
                filterState: nextState,
                isSearchPage,
                hideColorsFamily,
                colorsFamilyOptions,
                optionIdMapToRewrite,
                searchParams,
              });
            }}
          />
        ) : null}

        {filterOptions.map(x => {
          const { attribute_code, options, label } = x;

          if (
            attribute_code === 'aw_product_stock_condition' ||
            attribute_code === 'aw_general_size' ||
            attribute_code === 'category_id' ||
            attribute_code === COLORS_FAMILY_KEY ||
            attribute_code === 'color' ||
            (attribute_code === 'custom_option_color_ids' && !isSearchPage && !hideColorsFamily)
          ) {
            return null;
          }

          const itemCountToShow =
            attribute_code === 'custom_option_color_ids' || attribute_code === 'custom_option_size_ids' ? Infinity : 6;

          return (
            <FilterAsideItem
              defaultExpanded={filterState.some(x => x.attributeCode === attribute_code)}
              label={label}
              options={options}
              attributeCode={attribute_code}
              key={attribute_code}
              filterState={filterState}
              curCategoryUrlKey={curCategoryUrlKey}
              itemCountToShow={itemCountToShow}
              generateNextState={param =>
                generateNextState({
                  ...param,
                  filterState,
                  optionIdMapToRewrite,
                  isSearchPage,
                  hideColorsFamily,
                })
              }
              onChangeFilter={nextFilterState => {
                onSetFilterState(nextFilterState);
              }}
              isSearchPage={isSearchPage}
              onGenerateLinkHref={nextState => {
                return generateLinkHref({
                  categoryUrlKey: curCategoryUrlKey,
                  filterState: nextState,
                  isSearchPage,
                  hideColorsFamily,
                  colorsFamilyOptions,
                  optionIdMapToRewrite,
                  searchParams,
                });
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const FilterAsideItem = (props: {
  defaultExpanded: boolean;
  label: string;
  attributeCode: string;
  options: {
    count: number;
    label: string;
    swatch_data: { thumbnail: string | null; value: string | null } | null;
    value: string;
    parent_id: number;
    url_key: string;
  }[];
  curCategoryUrlKey: string;
  className?: string;
  filterState: FilterState;
  itemCountToShow?: number;
  generateNextState: (param: {
    attributeCode: string;
    value: string;
    label: string;
    prevStateIndex: number;
    prevActive: boolean;
  }) => FilterState;
  onChangeFilter: (nextFilterState: FilterState) => void;
  onGenerateLinkHref: (nextFilterState: FilterState) => string;
  isSearchPage: boolean;
}) => {
  const {
    defaultExpanded,
    label,
    options,
    attributeCode,
    curCategoryUrlKey,
    className,
    filterState,
    itemCountToShow = Infinity,
    generateNextState,
    onChangeFilter,
    onGenerateLinkHref,
    isSearchPage,
  } = props;
  const router = useLoadingRouter();

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showMore, setShowMore] = useState(false);
  const prevPropsRef = useRef({ defaultExpanded });

  if (prevPropsRef.current.defaultExpanded !== defaultExpanded) {
    prevPropsRef.current.defaultExpanded = defaultExpanded;
    setExpanded(defaultExpanded);
  }

  const handlePriceSelectChange = (prevStateIndex: number, min: number, max: number) => {
    const nextState = generateNextState({
      attributeCode,
      value: `${min}_${max}`,
      label: `${min}-${max}`,
      prevStateIndex,
      prevActive: false,
    });

    onChangeFilter(nextState);
    router.push(onGenerateLinkHref(nextState));
  };

  const renderFilterItem = () => {
    if (attributeCode === 'category_id') {
      const prevStateIndex = filterState.findIndex(x => x.attributeCode === attributeCode);
      const selectedCategoryOptions = prevStateIndex === -1 ? [] : filterState[prevStateIndex].options || [];

      return (
        <div
          className={clsx(!expanded && 'hidden', 'pb-[15px]')}
          key={attributeCode}
        >
          <div className={clsx('flex flex-col gap-y-[15px] ')}>
            {options.map((x, i) => {
              let href = `/${x.url_key}`;
              let isActive = x.url_key === curCategoryUrlKey;

              if (isSearchPage) {
                isActive = selectedCategoryOptions.some(option => option.value === x.value);
                const nextState = generateNextState({
                  attributeCode,
                  value: x.value,
                  label: x.label,
                  prevStateIndex,
                  prevActive: isActive,
                });
                href = onGenerateLinkHref(nextState);
              }

              return (
                <CategoryFilterItem
                  label={x.label}
                  key={x.value}
                  href={href}
                  count={x.count}
                  isActive={isActive}
                  mode='link'
                  className={clsx(!showMore && i >= itemCountToShow ? 'hidden' : '')}
                />
              );
            })}
          </div>
          {options.length > itemCountToShow ? (
            <button
              className='mt-[15px] text-sm underline'
              type='button'
              onClick={() => {
                setShowMore(x => !x);
              }}
            >
              {showMore ? 'Show Less' : 'Show More'}
            </button>
          ) : null}
        </div>
      );
    }

    if (attributeCode === 'price') {
      const prevStateIndex = filterState.findIndex(x => x.attributeCode === attributeCode);
      const selectedPriceOptions = prevStateIndex === -1 ? [] : filterState[prevStateIndex].options || [];

      return (
        <div
          className={clsx(!expanded && 'hidden', 'pb-[15px] pr-[25px]')}
          key={attributeCode}
        >
          <PriceSelect
            values={options.map(x => x.value)}
            filterStateRewrite={selectedPriceOptions[0]?.rewrite || ''}
            onAfterChange={(min, max) => {
              handlePriceSelectChange(prevStateIndex, min, max);
            }}
          />
        </div>
      );
    }

    if (attributeCode === 'custom_option_color_ids') {
      const prevStateIndex = filterState.findIndex(x => x.attributeCode === attributeCode);
      const selectedColorOptions = prevStateIndex === -1 ? [] : filterState[prevStateIndex].options || [];

      return (
        <div
          className={clsx(
            ' flex-row flex-wrap gap-2.5 pb-[15px] pl-2.5 pr-[25px] pt-[3px]',
            expanded ? 'flex' : 'hidden',
          )}
          key={attributeCode}
        >
          {options.map(x => {
            const isActive = selectedColorOptions.some(option => option.value === x.value);
            const nextState = generateNextState({
              attributeCode,
              value: x.value,
              label: x.label,
              prevStateIndex,
              prevActive: isActive,
            });

            return (
              <ColorFilterItem
                mode='link'
                label={x.label}
                swatch_data={x.swatch_data}
                isActive={isActive}
                href={onGenerateLinkHref(nextState)}
                onClick={() => {
                  onChangeFilter(nextState);
                }}
                key={x.value}
              />
            );
          })}
        </div>
      );
    }

    if (attributeCode === 'custom_option_size_ids') {
      const prevStateIndex = filterState.findIndex(x => x.attributeCode === attributeCode);
      const selectedSizeOptions = prevStateIndex === -1 ? [] : filterState[prevStateIndex].options || [];

      return (
        <div
          className={clsx(' flex-row flex-wrap gap-x-2 gap-y-2.5 pb-[15px] pr-[25px]', expanded ? 'flex' : 'hidden')}
          key={attributeCode}
        >
          {options.map(x => {
            const isActive = selectedSizeOptions.some(option => option.value === x.value);
            const nextState = generateNextState({
              attributeCode,
              value: x.value,
              label: x.label,
              prevStateIndex,
              prevActive: isActive,
            });

            return (
              <SizeFilterItem
                mode='link'
                label={x.label}
                isActive={isActive}
                href={onGenerateLinkHref(nextState)}
                onClick={() => {
                  onChangeFilter(nextState);
                }}
                key={x.value}
              />
            );
          })}
        </div>
      );
    }

    if (
      attributeCode === 'aw_dresses_silhouette' ||
      attributeCode === 'aw_dresses_hemline' ||
      attributeCode === 'aw_dresses_neckline' ||
      attributeCode === 'aw_dresses_backdetail' ||
      attributeCode === 'aw_dresses_straps_sleeves'
    ) {
      const prevStateIndex = filterState.findIndex(x => x.attributeCode === attributeCode);
      const selectedDressStyleOptions = prevStateIndex === -1 ? [] : filterState[prevStateIndex].options || [];

      return (
        <div
          className={clsx(!expanded && 'hidden', 'pb-[15px]')}
          key={attributeCode}
        >
          <div className={clsx(' grid grid-cols-3 gap-x-[7px] gap-y-5  pt-5 lg_gap-x-2 lg_gap-y-1.5 lg_pt-0')}>
            {options.map((x, i) => {
              const isActive = selectedDressStyleOptions.some(option => option.value === x.value);
              const nextState = generateNextState({
                attributeCode,
                value: x.value,
                label: x.label,
                prevStateIndex,
                prevActive: isActive,
              });

              return (
                <DressStyleFilterItem
                  mode='link'
                  label={x.label}
                  isActive={isActive}
                  href={onGenerateLinkHref(nextState)}
                  onClick={() => {
                    onChangeFilter(nextState);
                  }}
                  attributeLabel={label}
                  key={x.value}
                  className={clsx(!showMore && i >= itemCountToShow ? 'hidden' : '')}
                />
              );
            })}
          </div>
          {options.length > itemCountToShow ? (
            <button
              className='mt-[15px] text-sm underline'
              type='button'
              onClick={() => {
                setShowMore(x => !x);
              }}
            >
              {showMore ? 'Show Less' : 'Show More'}
            </button>
          ) : null}
        </div>
      );
    }

    const prevStateIndex = filterState.findIndex(x => x.attributeCode === attributeCode);
    const selectedOptions = prevStateIndex === -1 ? [] : filterState[prevStateIndex].options || [];

    return (
      <div
        className={clsx(!expanded && 'hidden', 'pb-[15px] ')}
        key={attributeCode}
      >
        <div className={clsx(' flex flex-col gap-y-[15px]')}>
          {options.map((x, i) => {
            const isActive = selectedOptions.some(option => option.value === x.value);
            const nextState = generateNextState({
              attributeCode,
              value: x.value,
              label: x.label,
              prevStateIndex,
              prevActive: isActive,
            });

            return (
              <DefaultFilterItem
                mode='link'
                label={x.label}
                isActive={isActive}
                href={onGenerateLinkHref(nextState)}
                onClick={() => {
                  onChangeFilter(nextState);
                }}
                count={x.count}
                key={x.value}
                className={clsx(!showMore && i >= itemCountToShow ? 'hidden' : '')}
              />
            );
          })}
        </div>
        {options.length > itemCountToShow ? (
          <button
            className='mt-[15px] text-sm underline'
            type='button'
            onClick={() => {
              setShowMore(x => !x);
            }}
          >
            {showMore ? 'Show Less' : 'Show More'}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div className={cn('border-t-1 border-solid border-E7DED5 ', className)}>
      <button
        type='button'
        className='flex min-h-[50px] w-full items-center justify-between pr-[25px]'
        onClick={() => {
          setExpanded(x => !x);
        }}
      >
        <span className='text-[17px] leading-[22px]'>
          {websiteEnv.isUnitedKingdom && label === 'Color' ? 'Colour' : label}
        </span>
        <span
          className={clsx(
            ' relative h-3 w-3 ',
            'before_absolute before_left-1/2 before_top-1/2 before_h-0.5 before_w-full before_-translate-x-1/2 before_-translate-y-1/2 before_bg-80583F before_content-[""] ',
            ' after_absolute after_left-1/2 after_top-1/2 after_h-0.5 after_w-full after_-translate-x-1/2 after_-translate-y-1/2  after_bg-80583F after_content-[""]',
            expanded ? 'after_rotate-0 ' : 'after_rotate-90',
            'after_transition-transform after_ease-in-out after_[transition-duration:300ms]',
          )}
        />
      </button>
      {renderFilterItem()}
    </div>
  );
};

export default FilterAside;
